import { ORGANISME_FORME_JURIDIQUE_TEXTS, useTextes } from '@common/config';
import { useCurrentCampagne } from '@common/hooks/campagne';
import { EFFECTIF_APPRENTI_PLACEHOLDER, NDA_PLACEHOLDER, SIREN_PLACEHOLDER } from '@common/schemas';
import { CODE_POSTAL_PLACEHOLDER } from '@common/schemas/etablissement';
import { EditOfaInfoFormResult, editOfaInfoFormSchema } from '@common/schemas/forms/organisme';
import {
  CampagneProjection,
  OrganismeDetailsDto,
  OrganismeUpdateBodyDto,
  useGetCampagneByAnneeQuery,
  useGetOrganismeForCampagneQuery, useGetOrganismeQuery,
  useUpdateOrganismeMutation
} from '@common/services';
import { DateField, DsfrModal, Form, FormField, NumberField, SelectField, YesNoRadioGroupField } from '@common/ui';
import { DateUtils } from '@common/utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { z, ZodObject } from 'zod';
import { FormMethodsRef } from '../../form';

export const EditOfaInfoModal = ({ modal, organisme, raisonSocialNdaReadonly, isFc }: EditOfaInfoModalProps) => {
  const t = useTextes('Commun', 'Formulaire');
  const { campagne } = useCurrentCampagne();

  const formSchemaRef = useRef<ZodObject>(editOfaInfoFormSchema);
  const buttonSubmitRef = useRef<HTMLButtonElement | null>(null);
  const formMethodsRef = useRef<FormMethodsRef<EditOfaInfoFormResult> | null>(null);

  const [openGroupe, setOpenGroupe] = useState<boolean>(false);
  const [openReseau, setOpenReseau] = useState<boolean>(false);

  const [updateOrganisme] = useUpdateOrganismeMutation();

  const {
    data: campagnePrecedente,
  } = useGetCampagneByAnneeQuery(
      { year: (campagne?.annee ?? 0) - 1, projection: CampagneProjection.ANNEE },
      { skip: !campagne?.annee },
  );

  const {
    data: organismeInscriptionId,
    isLoading: isLoadingOrganismeInscription,
  } = useGetOrganismeForCampagneQuery(
      { siren: organisme!.siren, campagneId: campagnePrecedente?.id! },
      { skip: !organisme?.siren || !campagnePrecedente?.id },
  );

  const {
    data: organismePrecedent,
    isLoading: isLoadingOrganismePrecedent,
  } = useGetOrganismeQuery(
      { id: organismeInscriptionId?.id! },
      { skip: !organismeInscriptionId?.id }
  )

  const resolveField = (current: string | null | undefined, previous: string | null | undefined): string =>
    current?.trim() || previous?.trim() || '';

  const resolveBoolean = (current: boolean | null | undefined, previous: boolean | null | undefined): 'true' | 'false' => (current ?? previous) ? 'true' : 'false';

  useEffect(() => {
    if (isFc) {
      formSchemaRef.current = editOfaInfoFormSchema.safeExtend({
        formeJuridique: z.string(),
        adresse: z.string(),
        codePostal: z.string(),
        ville: z.string(),
      }).partial();
    } else if (raisonSocialNdaReadonly) {
      formSchemaRef.current = editOfaInfoFormSchema.safeExtend({
        raisonSociale: editOfaInfoFormSchema.shape.raisonSociale.readonly(),
        nda: editOfaInfoFormSchema.shape.nda.readonly(),
      });
    } else {
      formSchemaRef.current = editOfaInfoFormSchema;
    }
  }, [raisonSocialNdaReadonly, isFc]);

  useEffect(() => {
    if (organisme.groupe || organismePrecedent?.groupe) setOpenGroupe(true);
    if (organisme.reseau || organismePrecedent?.reseau) setOpenReseau(true);
  }, [organisme, organismePrecedent]);

  const handleSubmit = async (result: EditOfaInfoFormResult) => {
    const {
      avaitActiviteApprentissage,
      horsLesMurs,
      dateDebutActivite,
      dateDebutExerciceComptable,
      dateFinExerciceComptable,
      hasGroupe, // form only
      groupe,
      hasReseau, // form only
      reseau,
      siren, // form only,
      cfa,
      sousTraitant,
      ...updatableFields
    } = result;

    await updateOrganisme({
      id: organisme.id,
      dto: {
        ...updatableFields,
        formeJuridique: updatableFields.formeJuridique || null,
        adresse: updatableFields.adresse || null,
        codePostal: updatableFields.codePostal || null,
        ville: updatableFields.ville || null,
        complementAdresse: updatableFields.complementAdresse || null,
        avaitActiviteApprentissage: (avaitActiviteApprentissage === 'true'),
        horsLesMurs: (horsLesMurs === 'true'),
        dateDebutActivite: new Date(dateDebutActivite),
        dateDebutExerciceComptable: new Date(dateDebutExerciceComptable),
        dateFinExerciceComptable: new Date(dateFinExerciceComptable),
        groupe: (hasGroupe === 'true') ? groupe : null,
        reseau: (hasReseau === 'true') ? reseau : null,
        cfa: (cfa === 'true'),
        sousTraitant: (sousTraitant === 'true'),
      } as OrganismeUpdateBodyDto,
    }).unwrap();

    modal.close();
  };

  const defaultValues = useMemo<EditOfaInfoFormResult>(() => ({
    raisonSociale: organisme.raisonSociale,
    siren: organisme.siren,
    nda: organisme.nda ?? undefined,
    effectifApprenti: organisme.effectifApprenti ?? undefined,

    denominationUsuelle: resolveField(organisme.denominationUsuelle, organismePrecedent?.denominationUsuelle),
    formeJuridique: resolveField(organisme.formeJuridique, organismePrecedent?.formeJuridique),
    adresse: resolveField(organisme.adresse, organismePrecedent?.adresse),
    complementAdresse: resolveField(organisme.complementAdresse, organismePrecedent?.complementAdresse),
    codePostal: resolveField(organisme.codePostal, organismePrecedent?.codePostal),
    ville: resolveField(organisme.ville, organismePrecedent?.ville),

    avaitActiviteApprentissage: resolveBoolean(organisme.avaitActiviteApprentissage, organismePrecedent?.avaitActiviteApprentissage),
    horsLesMurs: resolveBoolean(organisme.horsLesMurs, organismePrecedent?.horsLesMurs),
    dateDebutActivite: resolveField(
        organisme.dateDebutActivite && DateUtils.formatForInput(organisme.dateDebutActivite),
        organismePrecedent?.dateDebutActivite && DateUtils.formatForInput(organismePrecedent.dateDebutActivite),
    ),
    dateDebutExerciceComptable: resolveField(
        organisme.dateDebutExerciceComptable && DateUtils.formatForInput(organisme.dateDebutExerciceComptable),
        organismePrecedent?.dateDebutExerciceComptable && DateUtils.formatForInput(organismePrecedent.dateDebutExerciceComptable),
    ),
    dateFinExerciceComptable: resolveField(
        organisme.dateFinExerciceComptable && DateUtils.formatForInput(organisme.dateFinExerciceComptable),
        organismePrecedent?.dateFinExerciceComptable && DateUtils.formatForInput(organismePrecedent.dateFinExerciceComptable),
    ),
    partActiviteApprentissage: organisme.partActiviteApprentissage ?? organismePrecedent?.partActiviteApprentissage,
    hasGroupe: resolveBoolean(!!organisme.groupe || null, !!organismePrecedent?.groupe || null),
    groupe: resolveField(organisme.groupe, organismePrecedent?.groupe),
    hasReseau: resolveBoolean(!!organisme.reseau || null, !!organismePrecedent?.reseau || null),
    reseau: resolveField(organisme.reseau, organismePrecedent?.reseau),

    cfa: resolveBoolean(organisme.cfa, organismePrecedent?.cfa),
    sousTraitant: resolveBoolean(organisme.sousTraitant, organismePrecedent?.sousTraitant),
  }), [organisme, organismePrecedent]);

  if (isLoadingOrganismePrecedent || isLoadingOrganismeInscription) return null;

  return (
    <modal.Component title={''}
                     size="large"
                     topAnchor={false}
                     buttons={[
                       {
                         children: 'ANNULER',
                         className: 'text-red-400!',
                         doClosesModal: true,
                         priority: 'tertiary',
                         onClick: () => formMethodsRef.current?.reset(defaultValues),
                       },
                       {
                         children: 'VALIDER',
                         doClosesModal: false,
                         priority: 'primary',
                         onClick: () => buttonSubmitRef.current?.click(),
                       },
                     ]}>

      <div className={'flex flex-col p-6'}>

        <div className={'flex flex-col gap-6'}>

          <h4 className={'m-0!'}>Modification des informations de l'organisme</h4>

          <span className={'text-sm text-[#484D7A] mb-10'}>Les champs marqués d’un * sont obligatoires</span>
        </div>

        <Form<EditOfaInfoFormResult> schema={formSchemaRef.current}
                                     defaultValues={defaultValues}
                                     className={'flex flex-col gap-6'}
                                     onSubmit={handleSubmit}
                                     buttonSubmitRef={buttonSubmitRef}
                                     methodsRef={formMethodsRef}>

          <h6>Informations</h6>

          <FormField name="raisonSociale"
                     title="Raison sociale" />

          <FormField name="siren"
                     title="SIREN"
                     placeholder={SIREN_PLACEHOLDER} />

          <FormField name="nda"
                     title="NDA"
                     placeholder={NDA_PLACEHOLDER}
                     tooltip={t['DONNEE_OFA_COMP_NDA_INFO_BULLE_MSG']} />

          <NumberField name="effectifApprenti"
                       title="Effectif"
                       placeholder={EFFECTIF_APPRENTI_PLACEHOLDER} />

          <div className="separator" />

          <h6>Données organisme complémentaires</h6>

          <FormField name="denominationUsuelle"
                     title="Dénomination usuelle" />

          <SelectField name="formeJuridique"
                       title="Forme juridique"
                       options={Object.entries(ORGANISME_FORME_JURIDIQUE_TEXTS).map(([key, value]) => ({
                         value: key,
                         label: value,
                       }))} />

          <FormField name="adresse"
                     title="Adresse du siège social"
                     placeholder={'12 Rue des Vaux parés'}
                     tooltip={t['DONNEE_OFA_COMP_ADRESSE_INFO_BULLE_MSG']} />

          <FormField name="complementAdresse"
                     title="Complément d'adresse"
                     placeholder={'Bâtiment C'} />

          <FormField name="codePostal"
                     title="Code postal"
                     placeholder={CODE_POSTAL_PLACEHOLDER} />

          <FormField name="ville"
                     title="Ville"
                     placeholder={'Vannes'} />

          <div className="separator" />

          <YesNoRadioGroupField name="avaitActiviteApprentissage"
                                title={`Votre organisme a-t-il eu une activité apprentissage en ${campagne?.annee - 1} ?`} />

          <YesNoRadioGroupField name="horsLesMurs"
                                title={`L'organisme déclarant en ${campagne?.annee - 2} est-il un organisme dit "CFA hors-les-murs" ?`} />

          <DateField name="dateDebutActivite"
                     title="Date de début d'activité apprentissage de votre organisme"
                     placeholder={'25/02/2022'} />

          <DateField name="dateDebutExerciceComptable"
                     title="Date de début de l'exercice comptable de l'organisme déclarant"
                     placeholder={'25/02/2022'} />

          <DateField name="dateFinExerciceComptable"
                     title="Date de fin de l'exercice comptable de l'organisme déclarant"
                     placeholder={'25/02/2022'} />

          <FormField name="partActiviteApprentissage"
                     title="Part de l'activité apprentissage dans l'activité totale de votre organisme (%)"
                     placeholder={'100'} />

          <YesNoRadioGroupField name="hasGroupe"
                                title="L'organisme appartient-il à un groupe ?"
                                onChange={(value) => setOpenGroupe((value === 'true'))} />

          {(openGroupe) && (
            <FormField name="groupe"
                       title="Lequel ?"
                       placeholder="Galiléo"
                       forceRequiredIndicator />
          )}

          <YesNoRadioGroupField name="hasReseau"
                                title="L'organisme appartient-il à un réseau ?"
                                onChange={(value) => setOpenReseau(value === 'true')} />

          {(openReseau) && (
            <FormField name="reseau"
                       title="Lequel ?"
                       placeholder="Galiléo"
                       forceRequiredIndicator />
          )}

          <YesNoRadioGroupField name="cfa"
                                title="L’organisme est-il un CFA d’entreprise (au sens de la loi du 5 septembre 2018) ?" />

          <YesNoRadioGroupField name="sousTraitant"
                                title={`L’organisme intervient-il également en tant que "sous-traitant", au sens délégation de l'action de formation, pour le compte d'un autre organisme au titre de l'apprentissage ?`}
          />
        </Form>
      </div>
    </modal.Component>
  );
};

interface EditOfaInfoModalProps {
  organisme: OrganismeDetailsDto;
  modal: DsfrModal;
  raisonSocialNdaReadonly?: boolean;
  isFc?: boolean;
}